import { Component, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router, Event, RouterEvent } from '@angular/router';

import { TranslocoService } from '@jsverse/transloco';
import { ReplaySubject, Subject, Subscription, combineLatest, filter, takeUntil } from 'rxjs';

import { DropdownObject, SearchBox_onChangeOverrideEvent } from '@inmoment/the-kitchen';

import { AppService } from 'app/app.service';
import { FullStoryService, PageNames } from 'app/modules/full-story/services/full-story.service';
import { AnalyticsService, TimerFn } from 'app/providers/analytics.service';
import { AuthService, Permission } from 'app/providers/bridge-api/auth.service';
import {
  ConfigurationRoute,
  ConfigurationRouteService,
} from 'app/providers/semantria-api/configuration-route.service';
import {
  SemantriaConfiguration,
  ConfigurationService,
} from 'app/providers/semantria-api/configuration.service';
import { GroupService } from 'app/providers/semantria-api/group.service';
import { SemantriaUsersService } from 'app/providers/semantria-api/semantria-users.service';
import { CollectionService } from 'app/providers/storage-api/collection.service';
import { Project, ProjectService } from 'app/providers/storage-api/project.service';
import { AlertService } from 'app/services/alert.service';
import { DialogService } from 'app/services/dialog.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { SystemService } from 'app/services/system.service';
import { BaseConfigurationComponent } from '../base-configuration-component';
import { CreateConfigurationDialogOptions } from '../../components/create-configuration-drawer/create-configuration-drawer.component';
import { ConfigType } from '../../components/import-into-configuration-drawer/import-into-configuration-drawer.component';

export interface ConfigurationMoveEvent {
  originalGroupId: string;
  oldPermissionId: string;
  originalGroupName: string;
  newGroupName: string;
  newGroupId: string;
  newPermission: Permission;
}

enum ConfigSortTypes {
  DATE = 1,
  NAME = 2,
  LANG = 3,
}

@EnableClassTelemetry()
@Component({
  selector: 'app-configuration-list',
  templateUrl: './configuration-list.component.html',
  styleUrls: ['./configuration-list.component.scss'],
  standalone: false,
})
export class ConfigurationListComponent
  extends BaseConfigurationComponent
  implements OnInit, OnDestroy
{
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @ViewChild('configListActionsMenu') configListActionsMenu: MatMenuTrigger;
  @ViewChild('configurationMenu') configurationMenu: MatMenuTrigger;

  accountLimits: { [key: string]: number } = {};

  configFilterDropdownData: DropdownObject[] = [];

  public configFilter:
    | 'all'
    | 'active'
    | 'not-active'
    | 'in-layered'
    | 'in-cr'
    | 'in-auto'
    | 'deleted' = 'all';

  displayedColumns = ['lang', 'configName', 'tags', 'createdBy', 'lastUsed', 'actions'];
  filteredConfigurations: SemantriaConfiguration[] = [];

  projects: Project[];
  routes: ConfigurationRoute[];
  configRouteMap: Map<string, string[]> = new Map<string, string[]>();
  configCreatorMap: Map<string, string> = new Map<string, string>();
  configTooltipMap: Map<string, string>;
  configTooltipTranslationsMap: Map<string, string>;
  configMoveTooltipMap: Map<string, string>;
  configMoveTooltipTranslationsMap: Map<string, string>;

  searchValue: string;

  configurationsListSortData: DropdownObject[] = [];
  configurationsListSortTooltips = [];

  configsRightPlaceholder = { title: '', subtitle: '' };
  selectedConfig: SemantriaConfiguration;
  configurationSort = '1';
  isInDrawer = false;

  currentSort = ConfigSortTypes.DATE;
  reverseSort = false;
  sortId: string;
  sortIcon = 'sort_order_desc';
  sortTooltip = '';

  projectId$ = new ReplaySubject<string>();
  unsub$ = new Subject<void>();

  projectIdParam: string;
  selectedProjectName: string;
  isListLoading = true;

  showMoveConfigurationModal = false;
  moveConfig: SemantriaConfiguration;

  showImportConfigDialog = false;

  showCreateConfiguration = false;
  createConfigData: CreateConfigurationDialogOptions;

  routeNameAccessor = (route) => route.name;
  routeIdAccessor = (route) => route.route_id;
  reportTiming: TimerFn;
  routerSubscription = new Subscription();
  groupSubscription = new Subscription();

  internalProps = null;

  constructor(
    private route: ActivatedRoute,
    private projectService: ProjectService,
    private configRouteService: ConfigurationRouteService,
    private userService: SemantriaUsersService,
    protected titleService: Title,
    protected appService: AppService,
    protected authService: AuthService,
    protected systemService: SystemService,
    protected dialogService: DialogService,
    protected alertService: AlertService,
    protected configService: ConfigurationService,
    protected groupService: GroupService,
    protected collectionService: CollectionService,
    protected analyticsService: AnalyticsService,
    protected fullStoryService: FullStoryService,
    protected router: Router,
    protected translocoService: TranslocoService,
    private ngZone: NgZone
  ) {
    super(
      titleService,
      appService,
      authService,
      systemService,
      dialogService,
      alertService,
      configService,
      groupService,
      collectionService,
      router,
      route,
      translocoService
    );

    this.groupSubscription = this.groupService.selectedGroup$.subscribe((group) => {
      if (!group.group_id.includes(this.groupId) && this.groupId) {
        // the subbed group isn't our group ID and we have a group ID
        this.groupId = GroupService.shortenGroupId(group.group_id);
        this.setUpConfigList();
      }
    });
    this.projectIdParam = this.route.snapshot.queryParamMap.get('projectId');

    this.reportTiming = this.analyticsService.createTimer('ConfigList');
    this.fullStoryService.setPageVars(PageNames.configurations, {
      projectId: this.projectIdParam,
    });
  }

  async ngOnInit() {
    await super.onInit();

    this.translocoService
      .selectTranslate('configurations.list.search')
      .pipe(takeUntil(this.unsub$))
      .subscribe((translation) => {
        this.internalProps = {
          searchBox: {
            placeholder: translation,
            onChangeOverride: ($event: SearchBox_onChangeOverrideEvent) => {
              this.ngZone.run(() => {
                this.searchValue = $event.searchText;
                this.filterConfigurations();
              });
            },
          },
        };
      });

    this.translocoService
      .selectTranslate('configurations.list.title')
      .pipe(takeUntil(this.unsub$))
      .subscribe((translation) => this.setTitle(translation));

    this.translocoService
      .selectTranslateObject('configurations.list.filter')
      .pipe(takeUntil(this.unsub$))
      .subscribe((translations: { [key: string]: string }) => {
        this.configFilterDropdownData = Object.entries(translations).map(([k, v]) => ({
          title: v,
          id: k,
        }));
      });

    this.translocoService
      .selectTranslateObject('configurations.list.sort')
      .pipe(takeUntil(this.unsub$))
      .subscribe((translations: { [key: string]: string }) => {
        this.configurationsListSortData = [
          { title: translations['modified-desc'], id: '1' },
          { title: translations['modified-asc'], id: '2' },
          { title: translations['language-asc'], id: '3' },
          { title: translations['language-desc'], id: '4' },
          { title: translations['name-asc'], id: '5' },
          { title: translations['name-desc'], id: '6' },
        ];

        this.configurationsListSortTooltips = [
          translations['modified-desc-tooltip'],
          translations['modified-asc-tooltip'],
          translations['language-asc-tooltip'],
          translations['language-desc-tooltip'],
          translations['name-asc-tooltip'],
          translations['name-desc-tooltip'],
        ];

        if (this.sortId) {
          this.setSortTooltip();
        }
      });

    combineLatest([
      this.translocoService.selectTranslateObject('configurations.list.no-configurations'),
      this.translocoService.selectTranslateObject('configurations.list.no-configurations-create'),
    ])
      .pipe(takeUntil(this.unsub$))
      .subscribe(
        (translations) =>
          (this.configsRightPlaceholder = {
            title: translations[0],
            subtitle: translations[1],
          })
      );

    combineLatest([
      this.translocoService.selectTranslateObject('configurations.list.tooltip'),
      this.translocoService.selectTranslateObject('configurations.list.tooltip-move'),
    ])
      .pipe(takeUntil(this.unsub$))
      .subscribe((translations) => {
        this.configTooltipTranslationsMap = new Map(Object.entries(translations[0]));
        this.configTooltipMap = new Map();

        this.configMoveTooltipTranslationsMap = new Map(Object.entries(translations[1]));
        this.configMoveTooltipMap = new Map();
      });

    if (!this.groupService.getSelectedGroup()) {
      await this.groupService.refreshAndSelectActiveGroup();
    }

    await this.setUpConfigList();
  }

  ngOnDestroy() {
    this.routerSubscription.unsubscribe();
    this.groupSubscription.unsubscribe();
    this.unsub$.next();
  }

  private getDefaultConfiguration() {
    this.selectedConfig = null;

    if (this.route.firstChild) {
      const selectedConfigId = this.route.firstChild.snapshot.params['id'];
      this.selectedConfig = this.filteredConfigurations.find(
        (config) => config.id === selectedConfigId
      );

      if (!this.selectedConfig) {
        this.onConfigSelected(this.filteredConfigurations[0]);
      }
    } else {
      if (this.selectedConfig !== this.filteredConfigurations[0]) {
        this.onConfigSelected(this.filteredConfigurations[0]);
      }
    }
  }

  showConfigListItemTooltip(config: SemantriaConfiguration) {
    return (
      config.layer === true ||
      config?.tags.length > 0 ||
      this.autoAnalysisConfigs.includes(config.id)
    );
  }

  getConfigListItemTooltip(config: SemantriaConfiguration) {
    if (!this.configTooltipMap) {
      return;
    }

    if (!this.configTooltipMap.has(config.id)) {
      this.configTooltipMap.set(
        config.id,
        `<div >
    <p style="overflow-wrap: anywhere; padding-bottom: 6px;"><span class="kxi-text-600">${this.configTooltipTranslationsMap.get(
      'tags'
    )} </span>${
      config.tags ? config.tags.join(', ') : this.configTooltipTranslationsMap.get('n-a')
    }</p>
    <div style="display: grid; grid-template-columns: auto auto; column-gap: 32px; row-gap: 5px;">
    <p class="kxi-text-600">${this.configTooltipTranslationsMap.get('is-layered')}</p>
    <p>${
      config.layer
        ? this.configTooltipTranslationsMap.get('yes')
        : this.configTooltipTranslationsMap.get('no')
    }</p>
    <p class="kxi-text-600">${this.configTooltipTranslationsMap.get('routes')} </p>
    <p>${config.config_routes ? config.config_routes.length : 0}</p>
    <p class="kxi-text-600">${this.configTooltipTranslationsMap.get('has-auto-analysis')} </p>
    <p>${
      this.autoAnalysisConfigs.includes(config.id)
        ? this.configTooltipTranslationsMap.get('yes')
        : this.configTooltipTranslationsMap.get('no')
    }</p>
    </div>
    </div>`
      );
    }

    return this.configTooltipMap.get(config.id);
  }

  onConfigSelected(config: SemantriaConfiguration, event: any = undefined) {
    if (config) {
      const url = `groups/${this.groupId}/configurations/summary/${config.id}`;

      if (event?.metaKey || event?.detail?.metaKey) {
        window.open(url, '_blank');
        return;
      }
      this.selectedConfig = config;
      this.router.navigate([url]);
    } else {
      this.selectedConfig = undefined;
    }
  }

  onSortConfigList(id: string) {
    this.configurationSort = id;

    this.setSortTooltip(id);

    switch (id) {
      case '1':
        this.setSortType(id, ConfigSortTypes.DATE, true);
        this.sortIcon = 'sort_order_desc';
        break;
      case '2':
        this.setSortType(id, ConfigSortTypes.DATE, false);
        this.sortIcon = 'sort_order_asc';
        break;
      case '3':
        this.setSortType(id, ConfigSortTypes.LANG, false);
        this.sortIcon = 'sort_order_asc';
        break;
      case '4':
        this.setSortType(id, ConfigSortTypes.LANG, true);
        this.sortIcon = 'sort_order_desc';
        break;
      case '5':
        this.setSortType(id, ConfigSortTypes.NAME, false);
        this.sortIcon = 'sort_order_asc';
        break;
      case '6':
        this.setSortType(id, ConfigSortTypes.NAME, true);
        this.sortIcon = 'sort_order_desc';
        break;
    }
  }

  setSortTooltip(sortId = this.sortId) {
    this.sortTooltip = this.configurationsListSortTooltips[+sortId - 1];
  }

  public setSortType(sortId: string, sortType: ConfigSortTypes, reverseSort: boolean) {
    this.sortId = sortId;
    this.currentSort = sortType;
    this.reverseSort = reverseSort;

    this.sortConfigs(this.filteredConfigurations);
  }

  public sortConfigs(configs: SemantriaConfiguration[]) {
    switch (this.currentSort) {
      case ConfigSortTypes.DATE:
        configs.sort((a, b) =>
          this.cmp(a.modification_date, b.modification_date, this.reverseSort)
        );
        break;
      case ConfigSortTypes.NAME:
        configs.sort((a, b) => this.cmp(a.name, b.name, this.reverseSort));
        break;
      case ConfigSortTypes.LANG:
        configs.sort((a, b) => this.cmp(a.language_name, b.language_name, this.reverseSort));
        break;
    }
  }

  cmp(a: any, b: any, reverse = false): number {
    if (a === b) {
      return 0;
    }

    let order = 0;
    if (typeof a === 'string') {
      order = a.localeCompare(b);
    } else {
      order = a > b ? 1 : -1;
    }

    if (reverse) {
      order *= -1;
    }
    return order;
  }

  getCreator(config) {
    return this.configCreatorMap.get(config.creator_user_id);
  }

  moveConfigurationTooltip(config: SemantriaConfiguration) {
    if (!this.configMoveTooltipMap) {
      return;
    }

    if (!this.configMoveTooltipMap.has(config.id)) {
      this.configMoveTooltipMap.set(config.id, this.assembleMoveConfigurationTooltip(config));
    }

    return this.configMoveTooltipMap.get(config.id);
  }

  private assembleMoveConfigurationTooltip(config: SemantriaConfiguration) {
    if (config.removed) {
      return this.configMoveTooltipTranslationsMap.get('deleted');
    }
    if (config.config_routes && config.config_routes.length > 0) {
      return this.configMoveTooltipTranslationsMap.get('part-of-route');
    }
    if (this.autoAnalysisConfigs.includes(config.id)) {
      return this.configMoveTooltipTranslationsMap.get('auto-analysis');
    }
    if (config.layer || this.isPartOfLayeredConfig(config.id)) {
      if (this.isPartOfLayeredConfig(config.id)) {
        return this.configMoveTooltipTranslationsMap.get('part-of-layered');
      }
      return this.configMoveTooltipTranslationsMap.get('layered');
    }
    if (this.isReadOnly) {
      return this.configMoveTooltipTranslationsMap.get('readonly');
    }

    return this.configMoveTooltipTranslationsMap.get('moveable');
  }

  canMoveConfiguration(config: SemantriaConfiguration) {
    return (
      !this.isReadOnly &&
      !config.removed &&
      !(config.config_routes && config.config_routes.length > 0) &&
      !this.autoAnalysisConfigs.includes(config.id) &&
      !this.isPartOfLayeredConfig(config.id) &&
      !config.layer
    );
  }

  async setUpConfigList() {
    this.configCreatorMap.set('API', 'API');

    try {
      const [configs, projectsResponse, routes] = await Promise.all([
        this.configService.getConfigurations(true, this.groupService.getSelectedGroupId()),
        this.projectService.searchProjects(null, 100, true, true),
        this.configRouteService.getConfigurationRoutes(this.groupService.getSelectedGroupId()),
      ]);
      this.configurations = configs;
      this.projects = projectsResponse.items;
      this.routes = routes;
    } catch (err) {
      this.alertService.showErrorToast(
        this.translocoService.translate('configurations.list.error-initialize'),
        err
      );
      return;
    }

    this.configurations = this.configurations.filter((config) => !config.evaluation);
    this.configurations.forEach((config) => {
      config.tags = config.tags || [];
      config.config_routes = config.config_routes || [];

      config.tags.sort((t1, t2) => t1.localeCompare(t2));
      config.config_routes.sort((r1, r2) =>
        r1.config_route_name.localeCompare(r2.config_route_name)
      );

      this.configCreatorMap.set(config.creator_user_id, '');
    });

    for (const userId of Array.from(this.configCreatorMap.keys()).filter((id) => id !== 'API')) {
      this.userService.getUser(userId).then((res) => {
        const fullName = (res.first_name || '') + ' ' + (res.last_name || '');
        this.configCreatorMap.set(userId, fullName.trim() || res.email);
      });
    }

    this.routes.forEach((route) =>
      Object.values(route.configuration_map).forEach((configId) => {
        if (!this.configRouteMap.has(configId)) {
          this.configRouteMap.set(configId, []);
        }

        this.configRouteMap.get(configId).push(route.route_id);
      })
    );

    const loadTime = this.reportTiming('configList_loaded');
    this.fullStoryService.setPageVars(PageNames.configurations, {
      loadTime,
    });
    this.filterConfigurations();

    this.getDefaultConfiguration();
    this.routerSubscription = this.router.events
      .pipe(filter((e: Event | RouterEvent): e is RouterEvent => e instanceof NavigationEnd))
      .subscribe((e: RouterEvent) => {
        if (e.url === `/groups/${this.groupId}/configurations`) {
          this.setUpConfigList();
        }
      });

    this.isListLoading = false;
  }

  get isAccountAdmin() {
    return this.authService.isAccountAdmin;
  }

  private filterByString(configs: SemantriaConfiguration[]): SemantriaConfiguration[] {
    if (!configs) {
      return [];
    }
    if (!this.searchValue) {
      return [...configs];
    }

    const searchValue = this.searchValue.toLocaleLowerCase();
    const isAMatch: (s: string) => boolean = (s) =>
      s && s.toLocaleLowerCase().indexOf(searchValue) !== -1;

    return configs.filter((config: SemantriaConfiguration) => {
      if (config.id === this.searchValue || isAMatch(config.name)) {
        return true;
      }

      return config.tags.some((tag) => isAMatch(tag));
    });
  }

  private filterByEpxr(configs: SemantriaConfiguration[]) {
    return configs.filter((c: SemantriaConfiguration) => {
      switch (this.configFilter) {
        case 'all':
          return !c.removed;
        case 'active':
          return (
            !c.removed &&
            (c.config_routes.length > 0 || c.layer || this.autoAnalysisConfigs.includes(c.id))
          );
        case 'not-active':
          return (
            c.config_routes.length < 1 &&
            !c.layer &&
            !c.removed &&
            !this.autoAnalysisConfigs.includes(c.id)
          );
        case 'in-cr':
          return !c.removed && c.config_routes.length > 0;
        case 'in-auto':
          return !c.removed && this.autoAnalysisConfigs.includes(c.id);
        case 'in-layered':
          const layerChildrenIds = new Set(
            configs
              .filter((cfg: SemantriaConfiguration) => cfg.layer && !cfg.removed)
              .reduce((prev, curr) => {
                prev.push(...curr.parents);
                return prev;
              }, [])
          );

          return !c.removed && layerChildrenIds.has(c.id);
        case 'deleted':
          return c.removed;
        default:
          return c;
      }
    });
  }

  removeConfigurationAndFilter(configId: string) {
    this.configurations = this.configurations.filter((config) => config.id !== configId);
    this.filterConfigurations();
  }

  filterConfigurations() {
    if (this.configurations) {
      const filteredConfigs = this.filterByString(this.configurations);
      this.filteredConfigurations = this.filterByEpxr(filteredConfigs);
      if (this.selectedConfig) {
        if (!this.filteredConfigurations.some((conf) => conf.id === this.selectedConfig.id)) {
          this.onConfigSelected(this.filteredConfigurations[0]);
        }
      } else {
        this.getDefaultConfiguration();
      }

      this.onSortConfigList(this.configurationSort ?? '1');
      this.authService.extendSession();
    }
  }

  onCreateConfiguration(type: ConfigType, configDto?, inputFile?, fragment?) {
    this.showCreateConfiguration = true;
    this.createConfigData = {
      existingConfigs: this.configurations,
      groupId: this.groupService.getSelectedGroupId(),
      inputFile,
      sourceConfigDto: configDto,
      fragment,
      configType: type,
    };
  }

  onCloseCreateConfiguration(result) {
    if (result) {
      if (typeof result === 'boolean') {
        this.onImportConfiguration();
      } else {
        this.router.navigate(['/groups', this.groupId, 'configurations', result.configuration.id], {
          fragment: result.fragment,
        });
      }
    }

    this.showCreateConfiguration = false;
  }

  onMoveConfiguration(config: SemantriaConfiguration) {
    this.moveConfig = config;
    this.showMoveConfigurationModal = true;
  }

  async afterConfigurationMoved(res: ConfigurationMoveEvent) {
    this.showMoveConfigurationModal = false;
    if (res) {
      try {
        await this.groupService.removeGroupPermission(res.originalGroupId, res.oldPermissionId);
      } catch (err) {
        this.alertService.showErrorToast(
          this.translocoService.translate('configurations.list.move-remove-failed', {
            configurationName: this.moveConfig.name,
            groupName: res.originalGroupName,
          }),
          err
        );
        return;
      }
      try {
        await this.groupService.addGroupPermission(res.newGroupId, res.newPermission);
      } catch (err) {
        this.alertService.showErrorToast(
          this.translocoService.translate('configurations.list.move-add-failed', {
            configurationName: this.moveConfig.name,
            groupName: res.newGroupName,
          }),
          err
        );
        return;
      }
      this.alertService.showToast(
        this.translocoService.translate('configurations.list.move-success', {
          configurationName: this.moveConfig.name,
          groupName: res.newGroupName,
        })
      );
      await this.groupService.refreshAndSelectActiveGroup();

      this.removeConfigurationAndFilter(this.moveConfig.id);
      this.configService.clearCache();
    }
  }

  onImportConfiguration() {
    this.showImportConfigDialog = true;
  }

  async onCloseImportConfigurationDialog(res) {
    this.showImportConfigDialog = false;
    if (res) {
      res.importedItem.notes = res.importedItem.notes || '';

      this.onCreateConfiguration(
        res.importedItem.layer ? 'layered' : 'standard',
        res.importedItem,
        res.fileItem.file,
        'settings'
      );
    }
  }

  beforeConfigurationDeleted() {}

  afterConfigurationCloned(clone: SemantriaConfiguration) {
    clone.tags = clone.tags || [];
    this.configurations.push(clone);
    this.filterConfigurations();
  }

  isPartOfLayeredConfig(configID: string): boolean {
    for (const config of this.configurations) {
      if (!config.removed && config.parents?.includes(configID)) {
        return true;
      }
    }

    return false;
  }

  openMenuTrigger() {
    this.configurationMenu.openMenu();
  }
}
